import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Switch, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch } from 'react-redux';
import { authApiClient } from '../../../utils/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { useTranslatedMessages } from '../../../hooks/useTranslatedMessages';

const EmailNotificationPreferencesSection = () => {
  const dispatch = useDispatch();
  const { settings: msg, snackbarMessages } = useTranslatedMessages();

  const [DocumentApproval, setDocumentApproval] = useState(false);
  const [DocumentRejection, setDocumentRejection] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [updatingPreference, setUpdatingPreference] = useState(null);

  const notificationMessages = msg?.account?.emailNotificationPreferences || {};

  useEffect(() => {
    const fetchNotificationPreferences = async () => {
      try {
        const response = await authApiClient.get('/me/notification-preferences');

        setDocumentApproval(response.data?.DocumentApproval ?? false);
        setDocumentRejection(response.data?.DocumentRejection ?? false);
      } catch (error) {
        setDocumentApproval(false);
        setDocumentRejection(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotificationPreferences();
  }, []);

  const handleToggle = async (preference, currentValue) => {
    const newValue = !currentValue;

    // Optimistic update
    if (preference === 'receive_on_document_approval') {
      setDocumentApproval(newValue);
    } else {
      setDocumentRejection(newValue);
    }

    setUpdatingPreference(preference);

    try {
      const response = await authApiClient.patch('/me/notification-preferences', {
        [preference]: newValue,
      });

      if (response.data) {
        if (preference === 'receive_on_document_approval' && typeof response.data.DocumentApproval === 'boolean') {
          setDocumentApproval(response.data.DocumentApproval);
        }

        if (preference === 'receive_on_document_rejection' && typeof response.data.DocumentRejection === 'boolean') {
          setDocumentRejection(response.data.DocumentRejection);
        }
      }
    } catch (error) {
      // Revert optimistic update
      if (preference === 'receive_on_document_approval') {
        setDocumentApproval(currentValue);
      } else {
        setDocumentRejection(currentValue);
      }

      dispatch(
        showSnackbar({
          message: snackbarMessages.settings.notification_preferences_update_failed,
          severity: 'error',
        }),
      );
    } finally {
      setUpdatingPreference(null);
    }
  };

  return (
    <Box sx={{ mt: '2rem' }}>
      {/* Section title */}
      <Box
        sx={{
          mb: '1rem',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      />
      <Box sx={{ mb: '1rem' }}>
        <Typography variant="h6">{notificationMessages.title}</Typography>
      </Box>

      {/* Notification settings table */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '38px',
            px: '0.75rem',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
            }}
          >
            {notificationMessages.setting || 'SETTING'}
          </Typography>

          <Typography
            sx={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              mr: '0.75rem',
            }}
          >
            {notificationMessages.email || 'EMAIL'}
          </Typography>
        </Box>

        {/* Approval row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '42px',
            px: '0.75rem',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.15rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 300,
              }}
            >
              {notificationMessages.approvals}
            </Typography>

            <Tooltip title={notificationMessages.approvalsTooltip || ''}>
              <IconButton
                size="small"
                sx={{
                  p: '2px',
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: '14px',
                    color: 'text.secondary',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {isLoading ? (
            <CircularProgress size={18} sx={{ mr: '0.75rem' }} />
          ) : (
            <Switch
              checked={DocumentApproval}
              disabled={updatingPreference !== null}
              onChange={() => handleToggle('receive_on_document_approval', DocumentApproval)}
              size="small"
              sx={{
                mr: '0.75rem',
              }}
            />
          )}
        </Box>

        {/* Rejection row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: '42px',
            px: '0.75rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.15rem',
            }}
          >
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 300,
              }}
            >
              {notificationMessages.rejections}
            </Typography>

            <Tooltip title={notificationMessages.rejectionsTooltip || ''}>
              <IconButton
                size="small"
                sx={{
                  p: '2px',
                }}
              >
                <InfoOutlinedIcon
                  sx={{
                    fontSize: '14px',
                    color: 'text.secondary',
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>

          {isLoading ? (
            <CircularProgress size={18} sx={{ mr: '0.75rem' }} />
          ) : (
            <Switch
              checked={DocumentRejection}
              disabled={updatingPreference !== null}
              onChange={() => handleToggle('receive_on_document_rejection', DocumentRejection)}
              size="small"
              sx={{
                mr: '0.75rem',
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EmailNotificationPreferencesSection;
